import { ddmmaaaa } from './calendario';
import { isoWeekday } from './positions';

// Vigencia de un horario (0304): el tramo de la campaña en que rige. `null` en cualquiera de las dos
// puntas = la punta de la campaña. Lo que se muestra y lo que se valida en la pantalla de horarios
// sale de acá, para que la tabla, el formulario y el modal de baja digan lo mismo.

// es-PE: «set», no «sep».
const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
const DIA_CORTO = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];

/** `2026-10-16` → `16-oct`. */
export function diaMes(fecha: string): string {
  const [, m, d] = fecha.split('-');
  return `${d}-${MES_CORTO[Number(m) - 1]}`;
}

/** `2026-10-17` → `sáb 17/10`. Para listar jornadas: el día de la semana es lo que se reconoce. */
export function diaSemanaYFecha(fecha: string): string {
  const [, m, d] = fecha.split('-');
  return `${DIA_CORTO[isoWeekday(fecha) - 1]} ${d}/${m}`;
}

/** Suma `n` días (puede ser negativo) a una fecha YYYY-MM-DD, a mediodía UTC para no correrse. */
export function sumarDias(fecha: string, n: number): string {
  const [y, m, d] = fecha.split('-').map(Number);
  const r = new Date(Date.UTC(y, m - 1, d + n, 12, 0, 0));
  return `${r.getUTCFullYear()}-${String(r.getUTCMonth() + 1).padStart(2, '0')}-${String(r.getUTCDate()).padStart(2, '0')}`;
}

export interface CampaniaRango {
  start_date: string;
  end_date: string;
}

export interface VigenciaGuardada {
  vigente_desde: string | null;
  vigente_hasta: string | null;
}

export interface RangoVigente {
  desde: string;
  hasta: string;
  /** Sin ningún día: el horario se dio de baja desde el primer día de su vigencia. */
  vacio: boolean;
}

/** [max(inicio campaña, vigente_desde), min(fin campaña, vigente_hasta)] — lo mismo que expande `expandSchedule`. */
export function rangoVigente(h: VigenciaGuardada, c: CampaniaRango): RangoVigente {
  const desde = h.vigente_desde !== null && h.vigente_desde > c.start_date ? h.vigente_desde : c.start_date;
  const hasta = h.vigente_hasta !== null && h.vigente_hasta < c.end_date ? h.vigente_hasta : c.end_date;
  return { desde, hasta, vacio: desde > hasta };
}

/** La última baja registrada de un horario (`campaign_schedule_bajas`), si quien mira puede leerla. */
export interface BajaVista {
  desde: string;
  motivo: string;
}

export interface TextoVigencia {
  texto: string;
  /** `sin_dias`: dado de baja entero, la fila se atenúa y ya no ofrece «Dar de baja». */
  estado: 'recortada' | 'sin_dias';
}

/**
 * Cómo se lee la vigencia en la tabla de horarios. `null` = rige toda la campaña.
 *
 * La baja se nombra solo si es la que fijó el `vigente_hasta` actual (desde = hasta + 1): si
 * después alguien volvió a alargar el horario, esa baja ya no describe lo que rige. Quien no puede
 * leer las bajas (la RLS se las da solo al dueño y al jefe) ve las fechas, sin el motivo.
 */
export function textoVigencia(h: VigenciaGuardada, c: CampaniaRango, baja: BajaVista | null): TextoVigencia | null {
  const r = rangoVigente(h, c);
  const bajaVigente = baja !== null && h.vigente_hasta !== null && baja.desde === sumarDias(h.vigente_hasta, 1);
  const desdeRecortado = h.vigente_desde !== null && h.vigente_desde > c.start_date;
  const hastaRecortado = h.vigente_hasta !== null && h.vigente_hasta < c.end_date;

  if (bajaVigente) {
    const frase = `dado de baja desde ${diaMes(baja.desde)} (${baja.motivo})`;
    if (r.vacio || !desdeRecortado) return { texto: mayuscula(frase), estado: r.vacio ? 'sin_dias' : 'recortada' };
    return { texto: `Desde ${diaMes(r.desde)} · ${frase}`, estado: 'recortada' };
  }
  if (r.vacio) return { texto: 'Dado de baja', estado: 'sin_dias' };
  if (desdeRecortado && hastaRecortado) {
    return { texto: `Del ${diaMes(r.desde)} al ${diaMes(r.hasta)}`, estado: 'recortada' };
  }
  if (desdeRecortado) return { texto: `Desde ${diaMes(r.desde)}`, estado: 'recortada' };
  if (hastaRecortado) return { texto: `Hasta ${diaMes(r.hasta)}`, estado: 'recortada' };
  return null;
}

function mayuscula(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Los dos campos del formulario («Vigente desde» / «Vigente hasta»); vacío = sin recorte. */
export interface VigenciaDelFormulario {
  desde: string;
  hasta: string;
}

/** Valida los dos campos del formulario. `null` = se puede guardar. */
export function validarVigencia(v: VigenciaDelFormulario, c: CampaniaRango): string | null {
  const rango = `del ${ddmmaaaa(c.start_date)} al ${ddmmaaaa(c.end_date)}`;
  if (v.desde !== '' && (v.desde < c.start_date || v.desde > c.end_date)) {
    return `«Vigente desde» tiene que caer dentro de la campaña, ${rango}.`;
  }
  if (v.hasta !== '' && (v.hasta < c.start_date || v.hasta > c.end_date)) {
    return `«Vigente hasta» tiene que caer dentro de la campaña, ${rango}.`;
  }
  if (v.desde !== '' && v.hasta !== '' && v.desde > v.hasta) {
    return '«Vigente hasta» no puede ser anterior a «Vigente desde».';
  }
  return null;
}

/**
 * Qué cambio de vigencia de un horario YA GUARDADO no se puede hacer desde el formulario:
 *
 * - `acorta_final`: adelantar el fin es una baja. Por el formulario quedarían las jornadas de
 *   después colgadas hasta que alguien pulse «Regenerar», sin motivo, sin rastro y con las
 *   postulaciones pendientes sin rechazar. Se manda a «Dar de baja desde…».
 * - `atrasa_inicio`: correr el inicio deja afuera jornadas que pueden estar trabajadas. Quien llama
 *   decide si las hay (solo bloquea si el horario ya tiene jornadas antes del inicio nuevo).
 */
export function cambioDeVigenciaQueNoVaPorFormulario(
  anterior: VigenciaGuardada, nuevo: VigenciaDelFormulario, c: CampaniaRango,
): 'acorta_final' | 'atrasa_inicio' | null {
  const antes = rangoVigente(anterior, c);
  const despues = rangoVigente(
    { vigente_desde: nuevo.desde === '' ? null : nuevo.desde, vigente_hasta: nuevo.hasta === '' ? null : nuevo.hasta }, c,
  );
  if (!antes.vacio && despues.hasta < antes.hasta) return 'acorta_final';
  if (!antes.vacio && despues.desde > antes.desde) return 'atrasa_inicio';
  return null;
}

/**
 * La fecha que propone el modal de baja: la próxima jornada programada desde mañana (hora de Lima).
 * Si el horario no tiene jornadas generadas desde mañana, mañana mismo. Siempre dentro de la
 * campaña, que es lo único que el servidor acepta.
 */
export function fechaSugeridaDeBaja(proximaJornada: string | null, manana: string, c: CampaniaRango): string {
  const f = proximaJornada ?? manana;
  if (f < c.start_date) return c.start_date;
  if (f > c.end_date) return c.end_date;
  return f;
}
